// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { AlertService } from '@app/core/services/alert.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import {
  State,
  selectRoleDetail,
  LoadingRoleDetailsAction,
  selectPolicies,
  LoadingPolicyAction
} from '@app/access-management/state';
import { Observable, Subscription } from 'rxjs';
import { take, filter, map, switchMap } from 'rxjs/operators';
import {
  Policy,
  readParameterFromRouterState,
  RoleDetail,
  MaybeLoadable,
  Loaded,
  needsLoading,
  Loadable,
  GoAction,
  IAM_ROUTE,
  QueueAlertAction,
  AlertType,
  RolesService,
  Role
} from '@app/core';
import { SelectedRows } from '@app/shared/data-table-ajax/data-table-ajax.component';

const shouldLoadRole = (roleDetail: RoleDetail | undefined) =>
  roleDetail === undefined || roleDetail.role === undefined;

const shouldLoadPolicies = (policies: MaybeLoadable) => needsLoading<Loaded<Policy[]>>(policies);
const hasPoliciesState = (policies: MaybeLoadable) => !needsLoading<Loaded<Policy[]>>(policies);

@Component({
  selector: 'app-role-policies-form',
  templateUrl: './role-policies-form.component.html',
  styleUrls: ['./role-policies-form.component.scss']
})
export class RolePoliciesFormComponent implements OnInit, OnDestroy {
  readonly role$: Observable<Role | undefined>;
  readonly roleDetail$: Observable<RoleDetail>;
  readonly loadablePolicies$: Observable<Loadable>;
  readonly policies$: Observable<Policy[] | undefined>;
  private roleId: string;
  private submitSubscription: Subscription | undefined = undefined;
  selectedPolicies: Policy[] | undefined = undefined;

  policyColumns = [
    { name: 'Policy Name', prop: 'name' },
    { name: 'Policy Description', prop: 'description', cellClass: 'white-space-normal' }
  ];
  rolePolicies$: any;

  constructor(
    activatedRoute: ActivatedRoute,
    private store: Store<State>,
    private alertService: AlertService,
    private roleService: RolesService) {
    const roleId = readParameterFromRouterState(activatedRoute, 'id');
    if (roleId === undefined) {
      throw new Error('role id not in route');
    }
    this.roleId = roleId;
    const maybeRoleDetail$: Observable<RoleDetail | undefined> = this.store.pipe(select(selectRoleDetail(this.roleId)));

    maybeRoleDetail$
      .pipe(take(1), filter((roleDetail: RoleDetail | undefined) => shouldLoadRole(roleDetail)))
      .subscribe(() => store.dispatch(new LoadingRoleDetailsAction(this.roleId)));

    this.roleDetail$ = maybeRoleDetail$.pipe(filter<RoleDetail>(roleDetail => roleDetail !== undefined));

    this.role$ = this.roleDetail$.pipe(map(roleDetail => roleDetail.role));

    this.loadablePolicies$ = this.store.pipe(select(selectPolicies));
    this.loadablePolicies$
      .pipe(take(1), filter(shouldLoadPolicies))
      .subscribe(() => store.dispatch(new LoadingPolicyAction()));

    this.policies$ = this.loadablePolicies$.pipe(
      filter<Loaded<Policy[]> | Loadable>(hasPoliciesState),
      map(policyList => policyList.data)
    );

    this.rolePolicies$ = this.roleService.getRolePolicies(this.roleId)
      .pipe(map(list => [...list.map(row => ({...row, id: row.policy_id}))]));
  }

  ngOnInit() {}

  ngOnDestroy() {
    if (this.submitSubscription !== undefined) {
      this.submitSubscription.unsubscribe();
    }
  }

  onSelectPolicy(selectedPolicies: Policy[]) {
    this.selectedPolicies = selectedPolicies;
  }

  onSelectRows(rows: SelectedRows) {
    this.selectedPolicies = rows.selected;
  }

  submit() {

    const request = {
      policyIds: this.selectedPolicies && this.selectedPolicies.map( p => p.id)
    };

    this.roleService.updateRoleByPolicyIds(this.roleId, request).subscribe(
      _ => {
        this.goToRoleDetailsPage();
        this.store.dispatch(new LoadingRoleDetailsAction(this.roleId));
        this.alertService.success(`Policies have successfully been attached to this role.`);
      },
      err => {
        this.alertService.error(`Failed to attached policies to this role.`);
      }
    );
  }

  cancel() {
    // Todo: probably need to clear policy selection state
    this.goToRoleDetailsPage();
  }

  private getUpdateRolePolicyRequest(roleDetail: RoleDetail, newPolicies: Policy[] | undefined) {
    const policyIds = roleDetail.policies !== undefined ? roleDetail.policies.map(policy => policy.id) : [];
    const newPolicyIds = newPolicies !== undefined ? newPolicies.map(policy => policy.id) : [];
    return {
      policyIds: Array.from(new Set([...policyIds, ...newPolicyIds])) // remove dups in case
    };
  }

  private goToRoleDetailsPage() {
    this.store.dispatch(new GoAction(['/', IAM_ROUTE, 'roles', this.roleId]));
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/roles/role-policies-form/role-policies-form.component.ts